import { $t } from '@/i18n';
/**
 * 分支合并模态框组件
 *
 * @description
 * 用于处理分支间的代码合并和冲突解决
 *
 * 主要功能：
 * 1. 展示分支间的差异和冲突
 * 2. 提供冲突解决界面
 * 3. 提交合并结果到后端
 *
 * @example
 * ```typescript
 * import { openBranchMergeModal } from '@/components/branch/merge-modal';
 *
 * await openBranchMergeModal({
 *   projectId: 'proj123',
 *   branchId: 'feature-1',
 *   mergeBranchId: 'master',
 *   changes: conflictData,
 * });
 * ```
 */
import { defineComponent } from '@vue/composition-api';
import { message } from '@utils/wuji-message';
import ModalFactory from '@/utils/modalFactory';
import styles from './styles/merge-modal.module.scss';
import MergeView from './MergeView';
import AIConflictEditorView from './AIConflictEditorView';
import { applyPatches, findConflicts } from '@tencent/data-mapper';
import Sidebar from './Sidebar';
import { asyncNodeDiff3String, nodeDiff3String } from '@/utils/merge/merge-multi';
import { useStore } from '@/store';
import { useDataFormatter } from './hooks/useDataFormatter';
import { mergeService } from './services/mergeService';
import { isAIProjectInfo, getDefaultSelectedItem } from './utils/mergeUtils';
import { jsonToYaml } from '@/utils/merge/jsonToYaml';
import { MERGE_AI_RE_RESOLVE_HANDLER_KEY } from './constants';
export const MergeModal = defineComponent({
    name: 'MergeModal',
    props: {
        options: {
            type: Object,
            required: true,
        },
    },
    components: {
        MergeView,
        AIConflictEditorView,
    },
    emits: ['message'],
    provide() {
        return {
            [MERGE_AI_RE_RESOLVE_HANDLER_KEY]: () => this.handleReResolveAiConflict(),
        };
    },
    data() {
        return {
            visible: true,
            calculating: false,
            conflicts: [],
            mergedPatches: [],
            commitMessage: '',
            selectDetailChange: null,
            isSubmitting: false,
            currentBranchName: '',
            mergeBranchName: '',
            aiEditorIframeBootstrap: null,
            /** conflictResolved 后、mergeSuccess(projectInfo) 前：继续展示编辑器，避免空 tip 打开代码变更 */
            aiMergeAwaitingProjectInfo: false,
            /** 从 Diff 主动回到 AI 编辑器（含原本无冲突的合并项） */
            aiEditingInIframe: false,
        };
    },
    computed: {
        // 计算标题（分支合并：mergeBranch -> currentBranch）
        modalTitle() {
            if (this.currentBranchName && this.mergeBranchName) {
                return $t('k_b67c3745', { arg_0: this.mergeBranchName, arg_1: this.currentBranchName });
            }
            return $t('k_cff22f3a');
        },
        isShowingAiKeepAliveEditor() {
            if (this.aiEditingInIframe && this.aiEditorIframeBootstrap) {
                return true;
            }
            return Boolean(this.aiEditorIframeBootstrap
                && this.selectDetailChange
                && isAIProjectInfo(this.selectDetailChange.schemaKey)
                && (this.selectDetailChange.conflict || this.aiMergeAwaitingProjectInfo));
        },
        // 计算未解决的冲突数量
        unresolvedConflictCount() {
            return this.conflicts.filter((item) => item.conflict).length;
        },
        // 已解决的冲突项
        resolvedConflicts() {
            return this.conflicts.filter((item) => !item.conflict);
        },
        // 所有已合并项（包括已解决的冲突 + 原本无冲突的变更）
        allMergedItems() {
            return [...this.resolvedConflicts, ...this.mergedPatches];
        },
        // 是否可以提交（所有冲突都已解决）
        canSubmit() {
            return this.unresolvedConflictCount === 0;
        },
        // 提交按钮的 tooltip 提示
        submitTooltipTitle() {
            if (!this.canSubmit) {
                return $t('k_d9bc4f74');
            }
            if (!this.commitMessage) {
                return $t('k_529ef0ee');
            }
            return '';
        },
    },
    created() {
        this.fetchBranchNames();
        this.initChanges();
    },
    methods: {
        /**
         * 获取分支名称
         * 从 store 中获取分支列表，找到对应的分支名称用于显示
         */
        async fetchBranchNames() {
            try {
                const store = useStore();
                const { branchList } = store.state.branch;
                // 如果 store 中没有分支列表，则从服务端获取
                if (!branchList || branchList.length === 0) {
                    await store.dispatch('branch/fetchBranchList', {
                        projectId: this.options.projectId,
                    });
                }
                const currentBranch = store.state.branch.branchList.find((item) => item.branch === this.options.branchId);
                const mergeBranch = store.state.branch.branchList.find((item) => item.branch === this.options.mergeBranchId);
                this.currentBranchName = currentBranch?.name || this.options.branchId;
                this.mergeBranchName = mergeBranch?.name || this.options.mergeBranchId;
            }
            catch (err) {
                console.error($t('k_21f67470'), err);
                // 降级使用 branchId
                this.currentBranchName = this.options.branchId;
                this.mergeBranchName = this.options.mergeBranchId;
            }
        },
        /**
         * 初始化变更数据
         * 遍历后端返回的变更数据，进行冲突检测和分类
         */
        async initChanges() {
            if (!this?.options?.changes)
                return;
            this.calculating = true;
            try {
                const conflicts = [];
                const promises = [];
                const mergedPatches = [];
                /**
                 * 处理单个变更项
                 */
                const processChange = async ({ schemaKey, objectId, value }) => {
                    const objectKey = `${schemaKey}.${objectId}`;
                    let hasConflict = value.conflict;
                    let content;
                    if (value.conflict) {
                        const result = await this.formatConflictItem(value);
                        // AI 项目信息默认保持冲突状态，需要特殊处理
                        hasConflict = isAIProjectInfo(schemaKey) ? true : result.conflict;
                        content = result.result;
                    }
                    else {
                        content = this.formatMergeItem(value);
                        if (isAIProjectInfo(schemaKey)) {
                            const { minePatches, theirsPatches } = value.patches || {};
                            const { baseData } = value;
                            content = {
                                ...content,
                                oursData: applyPatches(baseData, minePatches),
                                theirsData: applyPatches(baseData, theirsPatches),
                            };
                        }
                    }
                    const data = {
                        content,
                        key: objectKey,
                        schemaTitle: value.schemaTitle,
                        title: value.title,
                        conflict: hasConflict,
                        schemaKey: value.schemaKey,
                        id: objectId,
                        sectionType: hasConflict ? 'unresolved' : 'merged',
                        wasConflict: hasConflict,
                    };
                    // 分类处理
                    if (hasConflict) {
                        conflicts.push(data);
                    }
                    else {
                        // 只有内容真正发生变化才添加到合并列表
                        if (data.content.before !== data.content.after) {
                            mergedPatches.push(data);
                        }
                    }
                };
                // 遍历所有变更项
                Object.entries(this.options.changes).forEach(([schemaKey, objectMap]) => {
                    Object.entries(objectMap).forEach(([objectId, value]) => {
                        promises.push(processChange({ schemaKey, objectId, value }));
                    });
                });
                await Promise.all(promises);
                this.conflicts = conflicts;
                this.mergedPatches = mergedPatches;
                // 任意 AI 合并项均预建 keep-alive iframe（含无冲突自动合并）
                const aiItem = conflicts.find(item => isAIProjectInfo(item.schemaKey))
                    || mergedPatches.find(item => isAIProjectInfo(item.schemaKey));
                this.aiEditorIframeBootstrap = aiItem ? { ...aiItem.content } : null;
                // 检查是否有需要合并的内容
                if (this.conflicts.length === 0 && this.mergedPatches.length === 0) {
                    message.warning($t('k_c855e813'));
                    this.handleClose();
                    return;
                }
                // 选择默认显示的项（使用工具函数）
                this.selectDetailChange = getDefaultSelectedItem(this.conflicts, this.mergedPatches);
            }
            catch (err) {
                console.error($t('k_d8fd8b86'), err);
                message.error($t('k_b55c2cae'));
                this.visible = false;
            }
            finally {
                this.calculating = false;
            }
        },
        /**
         * 将 JSON 对象转换为 YAML 字符串
         * 使用 useDataFormatter hook 的方法
         */
        formatToYAML(data) {
            const { formatToYAML } = useDataFormatter();
            return formatToYAML(data);
        },
        /**
         * 将 YAML 字符串转换为 JSON 对象
         * 使用 useDataFormatter hook 的方法
         */
        formatToJSON(data) {
            const { formatToJSON } = useDataFormatter();
            return formatToJSON(data);
        },
        /**
         * 格式化有冲突的项
         *
         * 使用 diff3 算法进行三方合并：
         * 1. 基准数据 + 当前分支补丁 = 当前分支数据
         * 2. 基准数据 + 合并分支补丁 = 合并分支数据
         * 3. 使用 diff3 合并，如果仍有冲突则保留冲突标记
         */
        async formatConflictItem(data) {
            const { minePatches, theirsPatches } = data.patches || {};
            const { baseData } = data;
            // 应用补丁得到两个分支的数据
            const currentBranchData = applyPatches(baseData, minePatches);
            const mergeBranchData = applyPatches(baseData, theirsPatches);
            // 转换为 YAML 格式
            const base = this.formatToYAML(baseData);
            const local = this.formatToYAML(currentBranchData);
            const remote = this.formatToYAML(mergeBranchData);
            // 根据 URL 参数决定是否使用 Web Worker
            const { formatDiff3Result } = useDataFormatter();
            const useWorker = window.location.search.indexOf('worker=0') === -1;
            const useDiff3 = window.location.search.indexOf('diff3=1') !== -1;
            const diffFunction = useWorker ? asyncNodeDiff3String : nodeDiff3String;
            const mergedResult = await diffFunction({
                base,
                local,
                remote,
                type: 'yaml',
                localLabel: this.currentBranchName,
                remoteLabel: this.mergeBranchName,
            }, { returnConflict: true, useDiff3 });
            return {
                conflict: mergedResult.conflict,
                result: {
                    before: this.formatToYAML(currentBranchData),
                    after: formatDiff3Result(mergedResult.result),
                    oursData: currentBranchData,
                    theirsData: mergeBranchData,
                },
            };
        },
        /**
         * 格式化无冲突的合并项
         *
         * 直接合并两个分支的补丁：
         * 1. 使用 findConflicts 合并补丁列表
         * 2. 应用合并后的补丁得到最终数据
         */
        formatMergeItem(data) {
            const { minePatches, theirsPatches } = data.patches || {};
            const { mergedPatches: combinedPatches } = findConflicts(minePatches, theirsPatches);
            const { baseData } = data;
            const beforeData = applyPatches(baseData, minePatches);
            const afterData = applyPatches(baseData, combinedPatches);
            return {
                before: this.formatToYAML(beforeData),
                after: this.formatToYAML(afterData),
            };
        },
        handleClose() {
            this.$nextTick(() => {
                this.visible = false;
                this.$emit('message', {
                    success: false,
                    message: 'cancel',
                });
            });
        },
        handleShowDetailChange(data) {
            this.selectDetailChange = data;
            // 侧栏切换时退出 iframe 编辑模式，以便展示 Diff / 其他合并项
            if (this.aiEditingInIframe) {
                this.aiEditingInIframe = false;
            }
        },
        /**
         * iframe conflictResolved / mergeSuccess：只更新父页侧栏 + 提交按钮，不改动 iframe。
         * resolved:false 表示落盘失败，把 AI 项打回未解决列表。
         * awaitingProjectInfo：乐观已解决但仍无 tip，继续占着编辑器，等 projectInfo 再露代码变更。
         */
        handleAiEditorChange(result) {
            const base = [...this.conflicts, ...this.mergedPatches]
                .find(item => isAIProjectInfo(item.schemaKey));
            if (!base) {
                return;
            }
            const data = typeof result === 'object' ? result.data : result;
            // conflictResolved 传空 data：不覆盖 after（避免用冲突 YAML 当合并结果）
            const content = {
                ...base.content,
                ...(data ? { after: data } : {}),
            };
            // 落盘失败打回：移回 conflicts 列表
            if (typeof result === 'object' && result.resolved === false) {
                this.aiMergeAwaitingProjectInfo = false;
                this.aiEditingInIframe = false;
                this.mergedPatches = this.mergedPatches.filter(item => item.key !== base.key);
                const unresolvedItem = {
                    ...base,
                    conflict: true,
                    sectionType: 'unresolved',
                    wasConflict: true,
                    content,
                };
                const existingIndex = this.conflicts.findIndex(item => item.key === base.key);
                if (existingIndex === -1) {
                    this.conflicts = [...this.conflicts, unresolvedItem];
                }
                else {
                    this.conflicts = [
                        ...this.conflicts.slice(0, existingIndex),
                        unresolvedItem,
                        ...this.conflicts.slice(existingIndex + 1),
                    ];
                }
                this.selectDetailChange = unresolvedItem;
                return;
            }
            if (typeof result === 'object' && typeof result.awaitingProjectInfo === 'boolean') {
                this.aiMergeAwaitingProjectInfo = result.awaitingProjectInfo;
                if (!result.awaitingProjectInfo) {
                    this.aiEditingInIframe = false;
                }
            }
            else if (data) {
                this.aiMergeAwaitingProjectInfo = false;
                this.aiEditingInIframe = false;
            }
            const isResolved = typeof result === 'object' && result.resolved;
            this.handleChangeMergeItem({
                ...base,
                conflict: isResolved ? false : base.conflict,
                content,
            });
        },
        handleChangeMergeItem(data) {
            // 优化：根据 conflict 状态更新项目所属列表
            const index = this.conflicts.findIndex(item => item.key === data.key);
            if (index !== -1) {
                if (!data.conflict) {
                    // 已解决冲突：从 conflicts 移到 mergedPatches，标记为 resolved
                    this.conflicts = this.conflicts.filter(item => item.key !== data.key);
                    const resolvedItem = {
                        ...data,
                        sectionType: 'resolved',
                        wasConflict: true,
                    };
                    this.mergedPatches = [...this.mergedPatches, resolvedItem];
                    this.selectDetailChange = resolvedItem;
                }
                else {
                    // 未解决：仅更新 conflicts 中的数据
                    this.conflicts = [
                        ...this.conflicts.slice(0, index),
                        { ...data, sectionType: 'unresolved' },
                        ...this.conflicts.slice(index + 1),
                    ];
                    this.selectDetailChange = data;
                }
            }
            else {
                // 如果在 mergedPatches 中找到（编辑已合并的项）
                const mergedIndex = this.mergedPatches.findIndex(item => item.key === data.key);
                if (mergedIndex !== -1) {
                    this.mergedPatches = [
                        ...this.mergedPatches.slice(0, mergedIndex),
                        data,
                        ...this.mergedPatches.slice(mergedIndex + 1),
                    ];
                    this.selectDetailChange = data;
                }
            }
        },
        /**
         * 查找当前合并中的 AI 项目项
         */
        findAiMergeItem() {
            const fromSelection = this.selectDetailChange
                && isAIProjectInfo(this.selectDetailChange.schemaKey)
                ? this.selectDetailChange
                : null;
            return fromSelection
                || this.conflicts.find(item => isAIProjectInfo(item.schemaKey))
                || this.mergedPatches.find(item => isAIProjectInfo(item.schemaKey))
                || null;
        },
        /**
         * AI 项目：从 Diff 直接回到 gogo 合并编辑器 iframe（与是否有冲突无关）
         */
        handleReResolveAiConflict(data) {
            const aiItem = data || this.findAiMergeItem();
            if (!aiItem || !isAIProjectInfo(aiItem.schemaKey)) {
                return;
            }
            this.aiEditorIframeBootstrap = { ...aiItem.content };
            this.aiMergeAwaitingProjectInfo = false;
            this.aiEditingInIframe = true;
            this.selectDetailChange = aiItem;
        },
        /**
         * 处理提交按钮点击
         * 校验数据并提交到后端
         */
        async handleOk() {
            try {
                this.isSubmitting = true;
                // 校验提交描述
                if (!this.commitMessage) {
                    message.warn($t('k_529ef0ee'));
                    return;
                }
                // 校验冲突是否已全部解决
                if (this.unresolvedConflictCount > 0) {
                    message.warn($t('k_5bf0cfa1', { arg_0: this.unresolvedConflictCount }));
                    return;
                }
                // 新版：低代码 merge 前先触发 gogo 真保存，拿到最新 projectInfo 写回 AI 项
                const aiEditor = this.$refs.aiEditorView;
                if (this.aiEditorIframeBootstrap && aiEditor?.requestSave) {
                    try {
                        const projectInfo = await aiEditor.requestSave();
                        if (projectInfo) {
                            this.handleAiEditorChange({
                                data: jsonToYaml(projectInfo),
                                resolved: true,
                            });
                        }
                    }
                    catch (err) {
                        message.error($t('k_2c142eaa', {
                            arg_0: err?.message || $t('k_8120aba'),
                        }));
                        return;
                    }
                }
                // 获取所有已合并项
                const allChanges = this.allMergedItems;
                // 转换数据格式
                const data = [];
                let hasFormatError = false;
                allChanges.forEach((item) => {
                    try {
                        const value = this.formatToJSON(item.content.after);
                        data.push({
                            id: item.id,
                            schemaInfo: {
                                id: item.schemaKey,
                            },
                            compareData: value,
                        });
                    }
                    catch (err) {
                        message.error($t('k_e4fdcd4f', { arg_0: item.title }));
                        hasFormatError = true;
                    }
                });
                if (hasFormatError)
                    return;
                // 提交到服务端
                await this.submitMerge(data);
            }
            finally {
                this.isSubmitting = false;
            }
        },
        /**
         * 提交合并到后端
         * 使用 mergeService 进行 API 调用
         */
        async submitMerge(data) {
            const { projectId, branchId, mergeBranchId } = this.options;
            try {
                await mergeService.submitMerge({
                    projectId,
                    branchId,
                    mergeBranchId,
                    data,
                    desc: this.commitMessage,
                });
                this.$emit('message', {
                    success: true,
                    data: { data, desc: this.commitMessage },
                });
                // 刷新页面（非 master 分支）
                mergeService.refreshAfterMerge(branchId);
            }
            catch (err) {
                // 错误已在 mergeService 中处理
                throw err;
            }
        },
    },
    render() {
        return (<a-drawer onClose={this.handleClose} title={this.modalTitle} placement="bottom" wj-mod={$t('k_cb3bc158')} visible={this.visible} height="100%" wrap-class-name={styles.commit_drawer_container}>
        <div class={styles.container}>
          <Sidebar conflicts={this.conflicts} mergedPatches={this.mergedPatches} onChange={this.handleShowDetailChange} selectDetailChange={this.selectDetailChange}/>
          <div class={styles.editor_wrap_right}>
            <div class="relative flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* AI 编辑器 keep-alive：侧栏切换只显隐；mergeSuccess 不回写 iframe props */}
              {this.aiEditorIframeBootstrap && (<div class="h-full min-h-0" style={{ display: this.isShowingAiKeepAliveEditor ? 'block' : 'none' }}>
                  <AIConflictEditorView ref="aiEditorView" class={styles.editor_diff_detail} conflictItem={this.aiEditorIframeBootstrap} currentBranchLabel={this.currentBranchName} mergeBranchLabel={this.mergeBranchName} sourceBranchId={this.options.mergeBranchId} targetBranchId={this.options.branchId} onChange={this.handleAiEditorChange}/>
                </div>)}
              {this.selectDetailChange && !this.isShowingAiKeepAliveEditor && (<MergeView class="h-full" change={this.selectDetailChange} on={{
                    change: this.handleChangeMergeItem,
                }} currentBranch={this.currentBranchName} mergeBranch={this.mergeBranchName}/>)}
            </div>
            <div class={styles.commit_action}>
              <a-textarea class="flex-1" placeholder={$t('k_2f12b3e5')} vModel={this.commitMessage} rows="2"/>
              <div class={styles.commit_action_buttons}>
                <a-button loading={this.isSubmitting} onClick={this.handleClose} class="mr-3">
                  {$t('k_9e48cac4')}
                </a-button>
                <a-tooltip title={this.submitTooltipTitle} placement="top">
                  <a-button loading={this.isSubmitting} onClick={this.handleOk} type="primary" disabled={!this.canSubmit || !this.commitMessage}>
                    {this.canSubmit
                ? $t('k_a83a6196')
                : $t('k_5bf0cfa1', { arg_0: this.unresolvedConflictCount })}
                  </a-button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <a-modal title={$t('k_757f8e09')} visible={this.calculating} closable={false} centered={true} onOk={() => { }} onCancel={() => { }} footer={null}>
            <div class="flex justify-center p-5">
              <a-spin tip={$t('k_2a958058')}/>
            </div>
          </a-modal>
        </div>
      </a-drawer>);
    },
});
const open = ModalFactory(MergeModal);
export const openBranchMergeModal = async (options) => {
    await open(options);
};
